import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    User,
    ShieldAlert,
    History,
    UserX,
    Gavel,
    RefreshCw,
    Copy,
    FileText,
    CheckCircle2,
    Search
} from 'lucide-react';
import { useTickets } from '../../../contexts/TicketContext';

const ReportedPlayerCard = ({ ticket, onRegisterAction }) => {
    const { checkPlayerId } = useTickets();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [playerData, setPlayerData] = useState(null);
    const [error, setError] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [copiedField, setCopiedField] = useState(null);
    const hasFetched = React.useRef(false);

    const targetId = ticket.formData?.reportedPlayerId || ticket.reportedPlayerId;
    const targetCity = ticket.city || ticket.formData?.city;

    const fetchPlayer = async (showLoading = true) => {
        if (!targetId) {
            setLoading(false);
            return;
        }

        if (showLoading) setLoading(true);
        try {
            const result = await checkPlayerId(targetCity, targetId);
            if (result.found) {
                setPlayerData(result.player);
            } else {
                setPlayerData(null);
            }
        } catch (err) {
            console.error("Error checking player:", err);
            setError(true);
        } finally {
            if (showLoading) setLoading(false);
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        if (!targetId || hasFetched.current) {
            if (!targetId) setLoading(false);
            return;
        }

        const createdAt = new Date(ticket.createdAt || Date.now()).getTime();
        const now = Date.now();
        const timeSinceCreation = now - createdAt;
        const delay = Math.max(0, 5000 - timeSinceCreation);

        const timer = setTimeout(() => {
            if (!hasFetched.current) {
                fetchPlayer(true);
                hasFetched.current = true;
            }
        }, delay);

        return () => clearTimeout(timer);
    }, [targetId, targetCity, ticket.createdAt]);

    const handleRefresh = () => {
        setIsRefreshing(true);
        fetchPlayer(true);
    };

    const copyToClipboard = (text, field) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
    };

    if (!targetId) return null;

    if (loading) {
        return (
            <Card className="mb-6 bg-slate-900/40 border-white/10 backdrop-blur-md">
                <CardContent className="p-8 flex flex-col items-center justify-center space-y-4">
                    <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                    <p className="text-gray-400 text-sm animate-pulse">Buscando informações do jogador...</p>
                </CardContent>
            </Card>
        );
    }

    if (!playerData) {
        return (
            <Card className="mb-6 bg-slate-900/40 border-white/10 backdrop-blur-md overflow-hidden">
                <div className="bg-red-500/10 p-4 border-b border-white/5 flex items-center gap-3">
                    <UserX className="w-5 h-5 text-red-400" />
                    <span className="font-bold text-gray-200">Jogador não encontrado</span>
                </div>
                <CardContent className="p-6 text-center space-y-4">
                    <div className="bg-black/20 p-4 rounded-xl border border-dashed border-white/10">
                        <p className="text-gray-400 text-sm mb-1">ID Denunciado</p>
                        <p className="text-2xl font-black text-white">#{targetId}</p>
                    </div>
                    <p className="text-gray-500 text-xs">
                        Este ID não possui vínculo com nenhum usuário registrado no sistema.
                    </p>
                    <div className="pt-2">
                        <Button
                            onClick={() => onRegisterAction(targetId, targetCity)}
                            className="w-full bg-red-600 hover:bg-red-700 text-white font-bold h-12"
                        >
                            <Gavel className="w-4 h-4 mr-2" />
                            Registrar Ação Manual
                        </Button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="mb-6 bg-slate-900/60 border-white/10 backdrop-blur-xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600/20 to-indigo-600/20 px-4 py-3 border-b border-white/10">
                <h3 className="text-sm font-black text-white uppercase tracking-tighter flex items-center gap-2">
                    <Search className="w-4 h-4 text-blue-400" />
                    Jogador Denunciado
                </h3>
            </div>

            <CardContent className="p-0">
                {/* ID & Discord Info */}
                <div className="grid grid-cols-2 border-b border-white/5">
                    <div className="p-3 border-r border-white/5 hover:bg-white/5 transition-colors group relative" onClick={() => copyToClipboard(targetId, 'id')}>
                        <span className="text-[10px] text-gray-500 font-bold uppercase block mb-1">ID Denunciado</span>
                        <div className="flex items-center justify-between">
                            <span className="text-lg font-black text-white">#{targetId}</span>
                            {copiedField === 'id' ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-600 group-hover:text-blue-400 transition-colors" />}
                        </div>
                    </div>
                    <div className="p-3 hover:bg-white/5 transition-colors group relative" onClick={() => copyToClipboard(playerData.discordId, 'discord')}>
                        <span className="text-[10px] text-gray-500 font-bold uppercase block mb-1">Discord Registrado</span>
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-blue-400 truncate pr-2">{playerData.discordId}</span>
                            {copiedField === 'discord' ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-600 group-hover:text-blue-400 transition-colors" />}
                        </div>
                    </div>
                </div>

                {/* Profile Section */}
                <div className="p-6 flex flex-col items-center border-b border-white/5 bg-gradient-to-b from-white/[0.02] to-transparent">
                    <div className="relative mb-4">
                        <div className="w-24 h-24 rounded-3xl bg-indigo-500/20 flex items-center justify-center border border-white/10 shadow-2xl overflow-hidden ring-4 ring-indigo-500/10">
                            {playerData.avatar ? (
                                <img src={`https://cdn.discordapp.com/avatars/${playerData.discordId}/${playerData.avatar}.png`} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <User className="w-10 h-10 text-indigo-400" />
                            )}
                        </div>
                        <div className="absolute -bottom-2 -right-2 bg-green-500 w-6 h-6 rounded-lg border-4 border-[#0f1115] flex items-center justify-center">
                            <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                        </div>
                    </div>
                    <div className="text-center">
                        <h4 className="text-2xl font-black text-white tracking-tight">{playerData.name}</h4>
                        <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-1">Nome Real: <span className="text-gray-400">{playerData.realName}</span></p>
                    </div>
                </div>

                {/* Stats Card */}
                <div className="px-4 py-4 grid grid-cols-2 gap-4">
                    <div className="bg-red-500/10 rounded-2xl p-4 border border-red-500/20 group hover:bg-red-500/20 transition-all">
                        <div className="flex items-center justify-between mb-2">
                            <ShieldAlert className="w-4 h-4 text-red-500" />
                            <span className="text-[10px] text-red-400 font-black uppercase">Castigos</span>
                        </div>
                        <span className="text-3xl font-black text-white">{playerData.stats.totalPunishments}</span>
                    </div>
                    <div className="bg-blue-500/10 rounded-2xl p-4 border border-blue-500/20 group hover:bg-blue-500/20 transition-all">
                        <div className="flex items-center justify-between mb-2">
                            <FileText className="w-4 h-4 text-blue-500" />
                            <span className="text-[10px] text-blue-400 font-black uppercase">Tickets</span>
                        </div>
                        <span className="text-3xl font-black text-white">{playerData.stats.totalTickets}</span>
                    </div>
                </div>

                {/* Punishment History */}
                <div className="px-4 pb-4">
                    <div className="flex items-center gap-2 mb-3 px-1">
                        <History className="w-3 h-3 text-gray-500" />
                        <span className="text-[10px] text-gray-500 font-black uppercase tracking-widest">Últimas Punições</span>
                    </div>
                    <div className="space-y-2">
                        {playerData?.punishments && playerData.punishments.length > 0 ? (
                            playerData.punishments.slice(0, 3).map((p, i) => (
                                <div key={i} className="bg-black/40 rounded-xl p-3 border border-white/5 flex flex-col gap-2 hover:border-red-500/30 transition-colors">
                                    <div className="flex justify-between items-start">
                                        <Badge variant="outline" className="text-[10px] bg-red-500/10 border-red-500/30 text-red-400 px-2 py-0 border-none rounded">
                                            {p.type}
                                        </Badge>
                                        <span className="text-[10px] text-gray-600 font-mono">{p.date}</span>
                                    </div>
                                    <p className="text-xs text-gray-300 font-medium leading-relaxed italic">
                                        "{p.internalReason}"
                                    </p>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-4 text-gray-600 text-[10px] uppercase font-bold border border-dashed border-white/5 rounded-xl">
                                Limpo como um cristal
                            </div>
                        )}
                    </div>
                </div>

                {/* Ticket History */}
                <div className="px-4 pb-6">
                    <div className="flex items-center gap-2 mb-3 px-1">
                        <FileText className="w-3 h-3 text-gray-500" />
                        <span className="text-[10px] text-gray-500 font-black uppercase tracking-widest">3 Últimos Tickets Criados</span>
                    </div>
                    <div className="space-y-2">
                        {playerData?.lastTickets && playerData.lastTickets.length > 0 ? (
                            playerData.lastTickets.slice(0, 3).map((t, i) => (
                                <div
                                    key={i}
                                    className="p-3 bg-white/[0.03] rounded-xl border border-white/5 hover:bg-white/[0.08] hover:border-blue-500/30 transition-all cursor-pointer group"
                                    onClick={() => navigate(`/staff/tickets/${t.id}`)}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center space-x-2 overflow-hidden">
                                            <span className="text-[9px] bg-blue-500/10 text-blue-400 px-1.5 py-0.5 rounded uppercase font-black tracking-tighter border border-blue-500/20 whitespace-nowrap">
                                                {t.category ? t.category.replace(/_/g, ' ') : 'Geral'}
                                            </span>
                                            <span className="text-[10px] font-mono text-gray-400 font-bold whitespace-nowrap">
                                                #{t.id}
                                            </span>
                                        </div>
                                        <Badge className={`text-[8px] uppercase font-black px-1 py-0 border-none shrink-0 ${t.status === 'aberto' ? 'bg-green-500/20 text-green-400' :
                                            t.status === 'resolvido' ? 'bg-blue-500/20 text-blue-400' :
                                                'bg-gray-500/20 text-gray-400'
                                            }`}>
                                            {t.status.replace(/_/g, ' ')}
                                        </Badge>
                                    </div>
                                    <div className="mt-2 flex items-center text-[9px] text-gray-500 font-medium">
                                        <History className="h-2.5 w-2.5 mr-1 text-gray-600" />
                                        {t.date}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-4 text-gray-600 text-[10px] uppercase font-bold border border-dashed border-white/5 rounded-xl">
                                Nenhum ticket encontrado
                            </div>
                        )}
                    </div>
                </div>
            </CardContent>

            {/* Footer Buttons */}
            <div className="p-4 bg-black/40 border-t border-white/5 flex gap-3">
                <Button
                    onClick={() => onRegisterAction(targetId, targetCity)}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-black text-xs uppercase"
                >
                    <Gavel className="w-3 h-3 mr-2" />
                    Aplicar Punição
                </Button>
                <Button
                    variant="outline"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="flex-1 bg-white/5 border-white/10 text-gray-400 hover:text-white hover:bg-white/10 text-xs font-black uppercase"
                >
                    <RefreshCw className={`w-3 h-3 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                    Atualizar Dados
                </Button>
            </div>
        </Card>
    );
};

export default ReportedPlayerCard;
